import { useEffect, useMemo } from 'react'
import { BufferGeometry, Matrix4, Quaternion, TubeGeometry, Vector3 } from 'three'
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'

import type { RailInstanceData } from '@hooks'
import {
  buildRailsCurves,
  LEFT_TUBE_SEGMENTS,
  METAL_MATERIAL,
  METAL_MATERIAL_TRANSPARENT,
  RADIAL_SEGMENTS,
  RAIL_HEIGHT,
  RAIL_RADIUS,
  RAIL_SCALE_Y,
  RIGHT_TUBE_SEGMENTS,
} from '@utils'

const _identity = new Quaternion()

interface Props {
  instances: RailInstanceData[]
}

export function RailsMergedManager({ instances }: Props) {
  const { activeGeometry, transparentGeometry } = useMemo(() => {
    const activeGeos: BufferGeometry[] = []
    const transparentGeos: BufferGeometry[] = []

    for (const { curve, cellPosition, active } of instances) {
      const { leftCurve, rightCurve } = buildRailsCurves(curve)
      const offsetMat = new Matrix4().compose(
        new Vector3(cellPosition.x, RAIL_HEIGHT, cellPosition.z),
        _identity,
        new Vector3(1, RAIL_SCALE_Y, 1),
      )

      const leftGeo = new TubeGeometry(
        leftCurve,
        LEFT_TUBE_SEGMENTS,
        RAIL_RADIUS,
        RADIAL_SEGMENTS,
        false,
      )
      leftGeo.applyMatrix4(offsetMat)
      const rightGeo = new TubeGeometry(
        rightCurve,
        RIGHT_TUBE_SEGMENTS,
        RAIL_RADIUS,
        RADIAL_SEGMENTS,
        false,
      )
      rightGeo.applyMatrix4(offsetMat)

      if (active) {
        activeGeos.push(leftGeo, rightGeo)
      } else {
        transparentGeos.push(leftGeo, rightGeo)
      }
    }

    const activeGeometry =
      activeGeos.length > 0 ? BufferGeometryUtils.mergeGeometries(activeGeos) : null
    const transparentGeometry =
      transparentGeos.length > 0 ? BufferGeometryUtils.mergeGeometries(transparentGeos) : null

    activeGeos.forEach(g => g.dispose())
    transparentGeos.forEach(g => g.dispose())

    return { activeGeometry, transparentGeometry }
  }, [instances])

  useEffect(() => {
    return () => {
      activeGeometry?.dispose()
      transparentGeometry?.dispose()
    }
  }, [activeGeometry, transparentGeometry])

  return (
    <>
      {activeGeometry && <mesh receiveShadow geometry={activeGeometry} material={METAL_MATERIAL} />}
      {transparentGeometry && (
        <mesh
          renderOrder={-1}
          geometry={transparentGeometry}
          material={METAL_MATERIAL_TRANSPARENT}
        />
      )}
    </>
  )
}
